/**
 * Notifications Web Push pour le portail formateur.
 *
 * - Détecte le support navigateur (PushManager, ServiceWorker, Notification).
 * - Demande la permission, enregistre le SW, souscrit via PushManager.
 * - POST l'abonnement au backend (RTK Query).
 *
 * Adapté de `portail/hooks/usePushNotifications.ts` pour l'auth back-office.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  useLazyGetPushVapidKeyFormateurQuery,
  usePushSubscribeFormateurMutation,
  usePushUnsubscribeFormateurMutation,
} from '../../services/formateurApi';

const SW_PATH = '/sw.js';

type Permission = 'default' | 'granted' | 'denied' | 'unsupported';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf;
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export interface UsePushFormateurResult {
  supported: boolean;
  permission: Permission;
  enabled: boolean;
  loading: boolean;
  enable: () => Promise<boolean>;
  disable: () => Promise<void>;
}

export function usePushFormateur(): UsePushFormateurResult {
  const supported = isSupported();
  const [permission, setPermission] = useState<Permission>(
    supported ? (Notification.permission as Permission) : 'unsupported'
  );
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const [getVapidKey] = useLazyGetPushVapidKeyFormateurQuery();
  const [subscribe] = usePushSubscribeFormateurMutation();
  const [unsubscribe] = usePushUnsubscribeFormateurMutation();

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register(SW_PATH);
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setEnabled(!!sub);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm as Permission);
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;

      const { public_key } = await getVapidKey().unwrap();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        });
      }

      const json = sub.toJSON() as any;
      await subscribe({
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      }).unwrap();
      setEnabled(true);
      return true;
    } catch (e) {
      console.error('[Push formateur] enable error', e);
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported, getVapidKey, subscribe]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await unsubscribe({ endpoint: sub.endpoint }).unwrap(); } catch { /* ignore */ }
        await sub.unsubscribe();
      }
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [supported, unsubscribe]);

  return { supported, permission, enabled, loading, enable, disable };
}
